/**
 * Formato de montos (plan 7.3.5).
 *
 * Los montos viajan SIEMPRE como `amount_minor` entero + `currency` ISO 4217. El
 * exponente decimal depende de la divisa: CLP y JPY tienen exponente 0 (la unidad
 * menor es el peso / el yen), KWD y BHD tienen 3, y CLF tiene 4. Dividir siempre
 * entre 100 produciria un cobro mal mostrado por dos ordenes de magnitud en Chile.
 */

/** Divisas cuyo exponente ISO 4217 no es 2. El resto usa DEFAULT_EXPONENT. */
const MINOR_EXPONENT: Record<string, number> = {
  BHD: 3,
  BIF: 0,
  CLF: 4,
  CLP: 0,
  DJF: 0,
  GNF: 0,
  IQD: 3,
  ISK: 0,
  JOD: 3,
  JPY: 0,
  KMF: 0,
  KRW: 0,
  KWD: 3,
  LYD: 3,
  OMR: 3,
  PYG: 0,
  RWF: 0,
  TND: 3,
  UGX: 0,
  UYW: 4,
  VND: 0,
  VUV: 0,
  XAF: 0,
  XOF: 0,
  XPF: 0,
};

const DEFAULT_EXPONENT = 2;

/** Locale de respaldo cuando el runtime no reconoce el que pidio el SDK. */
const FALLBACK_LOCALE = 'es-CL';

/** Exponente decimal de la divisa; 2 para las que no estan en la tabla. */
export function currencyExponent(currency: string): number {
  return MINOR_EXPONENT[currency.toUpperCase()] ?? DEFAULT_EXPONENT;
}

/** Convierte unidades menores a unidades mayores respetando el exponente. */
export function toMajorUnits(amountMinor: number, currency: string): number {
  return amountMinor / 10 ** currencyExponent(currency);
}

function safeLocale(locale: string): string {
  try {
    new Intl.NumberFormat(locale);
    return locale;
  } catch {
    return FALLBACK_LOCALE;
  }
}

/**
 * Formatea un monto para mostrarlo al comprador.
 *
 * @param amountMinor monto en unidades menores de la divisa
 * @param currency codigo ISO 4217 de tres letras
 * @param locale locale de la sesion de checkout (es-CL por defecto)
 */
export function formatAmount(amountMinor: number, currency: string, locale: string): string {
  const code = currency.toUpperCase();
  const exponent = currencyExponent(code);
  const value = amountMinor / 10 ** exponent;
  const resolvedLocale = safeLocale(locale);

  try {
    return new Intl.NumberFormat(resolvedLocale, {
      style: 'currency',
      currency: code,
      minimumFractionDigits: exponent,
      maximumFractionDigits: exponent,
    }).format(value);
  } catch {
    // Divisa que el runtime no conoce: se degrada a "1.499.000 XYZ" en lugar de romper.
    const plain = new Intl.NumberFormat(resolvedLocale, {
      minimumFractionDigits: exponent,
      maximumFractionDigits: exponent,
    }).format(value);
    return `${plain} ${code}`;
  }
}
